import { useState } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get("website_alt") as string || "").trim();
    if (honeypot) {
      setFormStatus("success");
      setFormMessage("Thank you for subscribing!");
      return;
    }
    try {
      const res = await fetch("https://readdy.ai/api/form/d9sp95dlbrt1qcgaj7e0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ email: formData.get("email") as string || "" }).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (res.ok && parsed?.code === "OK") {
        setFormStatus("success");
        setFormMessage("Thank you for subscribing! Check your inbox for the latest BaseTao Spreadsheet updates.");
        setEmail("");
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
        setFormStatus("error");
        setFormMessage(serverMsg.includes("spam") ? "Something went wrong. Please try again." : (serverMsg || "Something went wrong. Please try again."));
      }
    } catch {
      setFormStatus("error");
      setFormMessage("Network error. Please try again later.");
    }
  };

  return (
    <footer className="bg-background-100 border-t border-background-200/60">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <Link to="/" className="inline-block mb-4">
              <span className="font-heading text-xl text-foreground-900">
                BaseTao<span className="text-primary-500">Sheet</span>
              </span>
            </Link>
            <p className="text-sm text-foreground-600 leading-relaxed max-w-xs">
              Your trusted resource for BaseTao Spreadsheet guides, reviews, shipping calculators, and buyer tools. Independent research updated for 2026.
            </p>
          </div>

          <div>
            <h4 className="font-heading text-base text-foreground-900 mb-4">SEO Guides</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/article/basetao-spreadsheet-guide" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                  Spreadsheet Guide
                </Link>
              </li>
              <li>
                <Link to="/article/basetao-review" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                  BaseTao Review
                </Link>
              </li>
              <li>
                <Link to="/article/basetao-shipping-cost" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                  Shipping Cost Guide
                </Link>
              </li>
              <li>
                <Link to="/article/basetao-warehouse-guide" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                  Warehouse Guide
                </Link>
              </li>
              <li>
                <Link to="/article/basetao-coupon-discount" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                  Coupon & Discounts
                </Link>
              </li>
              <li>
                <Link to="/article/is-basetao-legit" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                  Is BaseTao Legit?
                </Link>
              </li>
              <li>
                <Link to="/article/basetao-shopping-guide" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                  Shopping Guide
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-base text-foreground-900 mb-4">Popular Categories</h4>
            <ul className="space-y-2">
              {categories.slice(0, 7).map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-base text-foreground-900 mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/article/basetao-shopping-guide" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                  Shopping Guide
                </Link>
              </li>
              <li>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                  Visit Main Website
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-background-200/60">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="w-full md:max-w-md">
              <h4 className="font-heading text-base text-foreground-900 mb-2">Newsletter</h4>
              <p className="text-sm text-foreground-600 mb-3">Get the latest BaseTao Spreadsheet tips and updates.</p>
              <form
                onSubmit={handleNewsletterSubmit}
                data-readdy-form
                className="flex flex-col sm:flex-row gap-2"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="flex-1 px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="antispam-field"
                />
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-lg bg-primary-500 text-white hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  Subscribe
                </button>
              </form>
              {formStatus !== "idle" && (
                <p className={`text-xs mt-2 ${formStatus === "success" ? "text-primary-600" : "text-accent-600"}`}>
                  {formMessage}
                </p>
              )}
            </div>
            <p className="text-xs text-foreground-500 whitespace-nowrap">
              &copy; {new Date().getFullYear()} basetaosheet.site. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}